import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isTrackingOff, setTrackingOff, sendTestEvent, lastTrackingError } from '../lib/analytics';

interface Stats {
  totals: { event: string; total: number; visitors: number }[];
  daily: { day: string; visitors: number; sent: number; answered: number }[];
  pages: { path: string; views: number; visitors: number }[];
  sources: { source: string; visitors: number }[];
  devices: { device: string; visitors: number }[];
}

const RANGES = [
  { label: 'Today', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
];

// Friendly names for the tap events (data-track="…" in the code).
const CLICK_LABELS: Record<string, string> = {
  home_start_asking: 'Start asking (start page)',
  home_sign_in: 'Sign In (start page)',
  home_how_it_works: 'How it works (start page)',
  dash_new_session: 'New Session (dashboard)',
  dash_yhuu_test: 'Yhuu Test banner (dashboard)',
  create_add_question: 'Add another question',
  create_send_click: 'Send it anonymously (tap)',
  recipient_send_my_yhuu: '“Send my Yhuu Now” (after answering)',
  sent_prompt_test: 'Take The Yhuu Test (after sending)',
  dash_have_they_opened: '“Have they opened it?” (locked session)',
  dash_unlock_card: 'Friends-unlock card (dashboard)',
  unlock_share: 'Share my invite link',
  unlock_copy_link: 'Copy invite link',
  unlock_premium: 'Unlock with Premium (in the friends modal)',
  recipient_skip_question: 'Skip question (recipient)',
  guess_open: 'Guess who sent this (opened)',
  guess_unlock: 'See who they think you are (unlock tap)',
  dash_guess_pill: 'Guesses pill (dashboard)',
  reply_template: 'Quick reply chip (recipient)',
  report_open: 'Report this Yhuu (opened)',
  sent_prompt_dismiss: '“No one really gets me” (after sending)',
};
const prettyClick = (e: string) => {
  const key = e.replace('click:', '');
  if (key.startsWith('create_platform_')) return `Picked ${key.replace('create_platform_', '')}`;
  return CLICK_LABELS[key] || key;
};

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [off, setOff] = useState(isTrackingOff());
  const [tick, setTick] = useState(0);
  const [testMsg, setTestMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const { data, error } = await supabase.rpc('admin_analytics', { days });
      if (cancelled) return;
      if (error) setError(error.message || 'Could not load analytics.');
      else setStats(data as Stats);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [days, tick]);

  if (error) {
    return (
      <div className="bg-brand-card border border-brand-border rounded-2xl p-6 text-sm">
        <div className="font-bold text-white mb-2">Analytics isn't set up yet</div>
        <p className="text-zinc-500 leading-relaxed mb-3">
          Open Supabase → SQL Editor, paste the contents of <code>supabase/analytics.sql</code> (it's in your GitHub repo) and press Run. Then come back and refresh.
        </p>
        <p className="text-[11px] text-zinc-600 break-words">Details: {error}</p>
      </div>
    );
  }
  if (loading && !stats) return <div className="text-center py-16 text-zinc-600 text-sm">Loading…</div>;
  if (!stats) return null;

  const ev = (name: string) => stats.totals.find(t => t.event === name);
  const total = (name: string) => ev(name)?.total ?? 0;
  const visitors = (name: string) => ev(name)?.visitors ?? 0;
  const homeVisitors = stats.pages.find(p => p.path === '/')?.visitors ?? 0;
  const allVisitors = ev('page_view')?.visitors ?? 0;

  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '–');

  const cards = [
    { label: 'Visitors', value: allVisitors },
    { label: 'New signups', value: total('signup') },
    { label: 'Sessions sent', value: total('session_sent') },
    { label: 'Recipients opened', value: total('recipient_open') },
    { label: 'Recipients answered', value: total('recipient_answered') },
    { label: 'Push enabled', value: total('push_enabled') },
    { label: 'Joined via invite', value: total('referral_joined') },
    { label: 'Guesses made', value: total('guess_submitted') },
  ];

  const creatorFunnel = [
    { label: 'Visited start page', n: homeVisitors },
    { label: 'Tapped “Start asking”', n: visitors('click:home_start_asking') },
    { label: 'Signed up', n: visitors('signup') },
    { label: 'Sent a session', n: visitors('session_sent') },
  ];
  const recipientFunnel = [
    { label: 'Opened the link', n: visitors('recipient_open') },
    { label: 'Answered', n: visitors('recipient_answered') },
    { label: 'Tapped “Send my Yhuu Now”', n: visitors('click:recipient_send_my_yhuu') },
  ];

  const clicks = stats.totals.filter(t => t.event.startsWith('click:'));
  const maxDaily = Math.max(1, ...stats.daily.map(d => d.visitors));

  const Funnel = ({ title, steps }: { title: string; steps: { label: string; n: number }[] }) => (
    <div className="bg-brand-card border border-brand-border rounded-2xl p-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-4">{title}</div>
      <div className="space-y-3">
        {steps.map((s, i) => {
          const top = steps[0].n || 1;
          return (
            <div key={s.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-zinc-400">{s.label}</span>
                <span className="text-white font-bold">
                  {s.n}
                  {i > 0 && <span className="text-zinc-600 font-normal"> · {pct(s.n, steps[i - 1].n)} of previous</span>}
                </span>
              </div>
              <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, (s.n / top) * 100)}%`, background: 'linear-gradient(90deg,#e11d48,#ec4899)' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const List = ({ title, rows, empty }: { title: string; rows: { label: string; n: number }[]; empty: string }) => (
    <div className="bg-brand-card border border-brand-border rounded-2xl p-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-600">{empty}</div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.label} className="flex justify-between gap-3 text-xs">
              <span className="text-zinc-400 truncate">{r.label}</span>
              <span className="text-white font-bold shrink-0">{r.n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {RANGES.map(r => (
          <button
            key={r.days}
            onClick={() => setDays(r.days)}
            className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer transition-all ${
              days === r.days ? 'bg-brand-accent text-white' : 'bg-brand-card border border-brand-border text-zinc-500 hover:text-white'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-brand-card border border-brand-border rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-white">{c.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <Funnel title="Creators: from visit to sending" steps={creatorFunnel} />
      <Funnel title="Recipients: from link to their own session" steps={recipientFunnel} />

      <div className="bg-brand-card border border-brand-border rounded-2xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-4">Visitors per day</div>
        {stats.daily.length === 0 ? (
          <div className="text-xs text-zinc-600">No data yet.</div>
        ) : (
          <div className="flex items-end gap-1.5 h-28">
            {stats.daily.map(d => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full min-w-0" title={`${d.day}: ${d.visitors} visitors, ${d.sent} sent, ${d.answered} answered`}>
                <div className="text-[9px] text-zinc-500 mb-1">{d.visitors}</div>
                <div className="w-full rounded-t-md" style={{ height: `${Math.max(4, (d.visitors / maxDaily) * 80)}%`, background: 'linear-gradient(180deg,#ec4899,#e11d48)' }} />
                <div className="text-[9px] text-zinc-600 mt-1">{d.day.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <List title="What people tap" rows={clicks.map(c => ({ label: prettyClick(c.event), n: c.total }))} empty="No taps yet." />
      <List title="Top pages (visitors)" rows={stats.pages.map(p => ({ label: p.path, n: p.visitors }))} empty="No data yet." />
      <List title="Where visitors come from" rows={stats.sources.map(s => ({ label: s.source, n: s.visitors }))} empty="No data yet." />
      <List title="Devices" rows={stats.devices.map(d => ({ label: d.device, n: d.visitors }))} empty="No data yet." />

      <div className="bg-brand-card border border-brand-border rounded-2xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">This device</div>
        <p className="text-xs text-zinc-500 leading-relaxed mb-3">
          {off
            ? "Your own visits and taps on this device are NOT counted (so you don't skew the numbers). Turn counting on to test that tracking works."
            : 'Your own visits and taps on this device ARE being counted right now (testing mode). Turn it off again when you\'re done.'}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => { setTrackingOff(!off); setOff(!off); }}
            className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-accent text-white"
          >
            {off ? 'Count my visits (test)' : 'Stop counting my visits'}
          </button>
          <button
            onClick={() => setTick(t => t + 1)}
            className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-card border border-brand-border text-zinc-500"
          >
            Refresh numbers
          </button>
        </div>
        <button
          onClick={async () => { setTestMsg('Sending…'); setTestMsg(await sendTestEvent()); }}
          className="mt-2 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest cursor-pointer bg-brand-card border border-brand-border text-zinc-500"
        >
          Send test event
        </button>
        {testMsg && <p className="text-xs text-zinc-400 mt-3 break-words">{testMsg}</p>}
        {lastTrackingError() && (
          <p className="text-[11px] text-rose-400 mt-3 break-words">Last tracking error on this device: {lastTrackingError()}</p>
        )}
      </div>
      <p className="text-[11px] text-zinc-600 leading-relaxed">
        Numbers count distinct browsers, not people.
      </p>
    </div>
  );
}
